/**
 * Record agreed amount on a purchase order line whose rest was cancelled
 * (docs/features/order-to-invoice/98_cancel_rest_and_credit_update_spec.md §5.2):
 * what the supplier agreed to charge becomes the line's committed value, with
 * why. The view's `record_agreed_amount` carries the title, today's committed
 * value and the route it posts to; the server's refusal, bound to the field it
 * names, replaces the checks made as typed. `onRecord` resolves to the refusal
 * to show, or null once done.
 */
import { useId, useState } from "react";
import type {
  RecordAgreedAmount,
  RecordAgreedAmountRequest,
} from "@shared-core/data/order-to-invoice-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import type { OrderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import { FailureBanner, Field } from "./OrderDialogs";

const MAX_REASON_LENGTH = 500;

/** "150", "150.5" or "150.50" -> 15000 pence; null when it isn't an amount. */
function penceOf(pounds: string): number | null {
  const text = pounds.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
}

export function RecordAgreedAmountDialog({
  agreed,
  busy,
  onClose,
  onRecord,
}: {
  agreed: RecordAgreedAmount;
  busy: boolean;
  onClose: () => void;
  onRecord: (
    input: RecordAgreedAmountRequest,
  ) => Promise<OrderStepFailure | null>;
}) {
  const id = useId();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [failure, setFailure] = useState<OrderStepFailure | null>(null);
  const pence = penceOf(amount);
  const problems = {
    amount_pence:
      pence === null
        ? "Enter the amount, e.g. 150.00."
        : pence === agreed.committed_value_pence
          ? "That is what it is committed at already."
          : undefined,
    reason: !reason.trim()
      ? "Enter the reason."
      : reason.trim().length > MAX_REASON_LENGTH
        ? `The reason can be at most ${MAX_REASON_LENGTH} characters.`
        : undefined,
  };
  const errorOf = (field: "amount_pence" | "reason") =>
    failure?.fieldErrors[field] ?? (failure ? problems[field] : undefined);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record agreed amount</DialogTitle>
          <DialogDescription>
            {agreed.title} It is committed at{" "}
            {formatQuotedPrice(agreed.committed_value_pence)} now; the amount
            you record replaces it. The supplier&apos;s purchase invoice is
            matched to it.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const count = Object.values(problems).filter(Boolean).length;
            if (count > 0 || pence === null) {
              setFailure({
                summary:
                  count === 1
                    ? "One field needs correcting."
                    : `${count} fields need correcting.`,
                fieldErrors: {},
              });
              return;
            }
            const refused = await onRecord({
              amount_pence: pence,
              reason: reason.trim(),
            });
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          <FailureBanner failure={failure} />
          <Field
            id={`${id}-amount`}
            label="Agreed amount, ex VAT"
            error={errorOf("amount_pence")}
          >
            {(aria) => (
              <Input
                {...aria}
                inputMode="decimal"
                placeholder="£"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
          </Field>
          <Field id={`${id}-reason`} label="Why" error={errorOf("reason")}>
            {(aria) => (
              <Textarea
                {...aria}
                rows={2}
                value={reason}
                placeholder="They charge for the sheets already printed"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy}>
              Record it
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
